import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTheme } from "next-themes";
import { T, F, th as palette, I, Over } from "@/lib/bricks";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, StatusTabs, Controls, Toolbar, Search, List, Row, Empty, TextLink, Loading, humanize } from "@/components/calm/Calm";
import { useAuth } from "@/contexts/AuthContext";
import { bookShootHref } from "@/lib/navConfig";
import { BookMediaLink } from "@/components/listing/BookMedia";

const REVIEWABLE_STATUSES = ['completed', 'delivered'];

function StarPicker({ value, onChange, size = 22, readOnly = false, color }) {
  return (
    <div style={{ display:'flex', gap:4 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange && onChange(n)}
          style={{ background:'none', border:'none', padding:0, cursor: readOnly ? 'default' : 'pointer', lineHeight:0 }}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <I name="star" size={size} color={n <= value ? T.brick : color} sw={n <= value ? 2 : 1.5} />
        </button>
      ))}
    </div>
  );
}

function VendorReviewCard({ job, p, dark }) {
  const [review, setReview] = useState(undefined); // undefined = loading, null = none yet
  const [draftRating, setDraftRating] = useState(0);
  const [draftComment, setDraftComment] = useState('');
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setReview(undefined);
    setError('');
    api.get(`/jobs/${job.job_id}/review`).then(res => {
      if (cancelled) return;
      setReview(res.data || null);
      setDraftRating(res.data?.rating || 0);
      setDraftComment(res.data?.comment || '');
      setEditing(!res.data);
    }).catch(() => { if (!cancelled) { setReview(null); setEditing(true); } });
    return () => { cancelled = true; };
  }, [job.job_id]);

  const submit = async () => {
    if (!draftRating) { setError('Pick a rating first.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post(`/jobs/${job.job_id}/review`, { rating: draftRating, comment: draftComment || null });
      setReview({ rating: draftRating, comment: draftComment || null });
      setEditing(false);
      void res;
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Could not save review.');
    } finally {
      setSubmitting(false);
    }
  };

  if (review === undefined) return null;

  return (
    <div>
      <Over color={p.ink3}>Photographer review</Over>
      <div style={{ marginTop:8, padding:'14px 16px', border:`1px solid ${p.hair}`, borderRadius:2, display:'flex', flexDirection:'column', gap:10 }}>
        {editing ? (
          <>
            <StarPicker value={draftRating} onChange={setDraftRating} color={p.ink4} />
            <textarea
              value={draftComment}
              onChange={e => setDraftComment(e.target.value)}
              placeholder="Comment (optional)"
              rows={2}
              style={{ fontFamily:F.display, fontSize:13, color:p.ink, background:'transparent', border:`1px solid ${p.hair}`, borderRadius:2, padding:'8px 10px', resize:'vertical' }}
            />
            {error && <div style={{ fontSize:12, color:T.brick }}>{error}</div>}
            <div style={{ display:'flex', gap:8 }}>
              <button
                onClick={submit}
                disabled={submitting}
                style={{ padding:'8px 14px', background:T.ink, border:'none', borderRadius:2, fontSize:12, fontWeight:500, fontFamily:F.display, color:T.paper, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.6 : 1 }}
              >
                {submitting ? 'Saving…' : (review ? 'Update review' : 'Submit review')}
              </button>
              {review && (
                <button
                  onClick={() => { setEditing(false); setDraftRating(review.rating); setDraftComment(review.comment || ''); setError(''); }}
                  style={{ padding:'8px 14px', background:'transparent', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:12, fontFamily:F.display, color:p.ink3, cursor:'pointer' }}
                >
                  Cancel
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <StarPicker value={review.rating} readOnly size={18} color={p.ink4} />
            {review.comment && <div style={{ fontSize:13, color:p.ink2 }}>{review.comment}</div>}
            <button
              onClick={() => setEditing(true)}
              style={{ alignSelf:'flex-start', padding:0, background:'none', border:'none', fontSize:12, color:T.brick, cursor:'pointer', fontFamily:F.display }}
            >
              Edit review
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const GROUPS = [
  { key: 'all',         label: 'All' },
  { key: 'upcoming',    label: 'Upcoming',    statuses: ['scheduled', 'pending', 'assigned'] },
  { key: 'in_progress', label: 'In progress', statuses: ['in_progress', 'editing', 'processing'] },
  { key: 'done',        label: 'Done',        statuses: ['completed', 'delivered'] },
];
export const jobInGroup = (job, key) => {
  const g = GROUPS.find(x => x.key === key);
  return !g || !g.statuses || g.statuses.includes(String(job.status || '').toLowerCase());
};

export default function JobsPage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedJob, setSelectedJob] = useState(null);
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const p = palette(dark);
  const navigate = useNavigate();
  void agents;

  useEffect(() => {
    Promise.all([api.get("/jobs"), api.get("/agents")]).then(([jRes, aRes]) => {
      setJobs(jRes.data); setAgents(aRes.data); setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filteredJobs = jobs.filter(j => {
    if (!jobInGroup(j, statusFilter)) return false;
    if (search && !j.property_address.toLowerCase().includes(search.toLowerCase()) && !(j.agent_name||'').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const countFor = key => jobs.filter(j => jobInGroup(j, key)).length;

  if (loading) return <Loading />;

  return (
    <Page testid="jobs-page">
      <PageHeader
        title="Shoots"
        help="booking"
        helpLabel="Help with booking"
        subtitle={`${jobs.length} total`}
        action={<BookMediaLink href={bookShootHref({ user })} dark={dark} testId="new-job-btn" />}
      />

      <Stats items={[
        { label: 'Upcoming', value: countFor('upcoming') },
        { label: 'In progress', value: countFor('in_progress') },
        { label: 'Done', value: countFor('done') },
      ]} />

      <Controls>
        <StatusTabs testid="jobs-status-filter" value={statusFilter} onChange={setStatusFilter}
          options={GROUPS.map(g => ({ key: g.key, label: g.label }))} />
        <Toolbar>
          <Search testid="jobs-search" value={search} onChange={setSearch} placeholder="Search address or agent" />
        </Toolbar>
      </Controls>

      {filteredJobs.length === 0 ? (
        <Empty action={!search && jobs.length === 0 ? <TextLink href={bookShootHref({ user })}>Book media</TextLink> : null}>
          {search ? 'No shoots match that search.' : jobs.length === 0 ? 'No shoots yet. Booked shoots show up here with their delivery status.' : 'Nothing in this group right now.'}
        </Empty>
      ) : (
        <List testid="jobs-list">
          {filteredJobs.map(job => {
            const delivery = String(job.delivery_status || '').toLowerCase();
            return (
              <Row
                key={job.job_id}
                testid={`job-row-${job.job_id}`}
                onClick={() => setSelectedJob(job)}
                title={job.property_address?.split(',')[0]}
                meta={[job.agent_name, job.vendor_name || 'No photographer yet'].filter(Boolean)}
                next={[
                  job.created_at ? { text: `Booked ${fmtDay(job.created_at)}` } : null,
                  delivery && delivery !== String(job.status || '').toLowerCase() ? { text: `Delivery ${humanize(delivery).toLowerCase()}` } : null,
                  job.property_type || null,
                ].filter(Boolean)}
                status={humanize(job.status) || 'Pending'}
              />
            );
          })}
        </List>
      )}

      {/* Job Detail Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent style={{ maxWidth:480, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}` }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, letterSpacing:-0.3, color:p.ink }}>Shoot</DialogTitle>
            <DialogDescription style={{ fontFamily:F.mono, fontSize:12, color:p.ink3 }}>#{selectedJob?.job_id}</DialogDescription>
          </DialogHeader>
          {selectedJob && (
            <div style={{ display:'flex', flexDirection:'column', gap:20, paddingTop:8 }}>
              <div>
                <Over color={p.ink3}>Property</Over>
                <div style={{ fontSize:15, fontWeight:500, marginTop:8, color:p.ink }}>{selectedJob.property_address}</div>
                <div style={{ fontSize:12, color:p.ink3, marginTop:2 }}>{selectedJob.property_type}</div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                {[['Agent', selectedJob.agent_name], ['Photographer', selectedJob.vendor_name || 'Not assigned yet']].map(([l, v]) => (
                  <div key={l}>
                    <Over color={p.ink3}>{l}</Over>
                    <div style={{ fontSize:14, marginTop:8, color:p.ink }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div>
                  <Over color={p.ink3}>Status</Over>
                  <div style={{ marginTop:8, fontSize:14, color:p.ink }}>{humanize(selectedJob.status) || 'Pending'}</div>
                </div>
                <div>
                  <Over color={p.ink3}>Delivery</Over>
                  <div style={{ marginTop:8, fontSize:14, color:p.ink }}>{humanize(selectedJob.delivery_status) || 'Not started'}</div>
                </div>
              </div>
              {REVIEWABLE_STATUSES.includes(selectedJob.status) && selectedJob.assignee_id && (
                <VendorReviewCard key={selectedJob.job_id} job={selectedJob} p={p} dark={dark} />
              )}
              {selectedJob.transaction_id && (
                <div>
                  <Over color={p.ink3}>Deal</Over>
                  <div
                    onClick={() => navigate(`/transactions/${selectedJob.transaction_id}`)}
                    style={{ marginTop:8, fontSize:14, color:p.ink, cursor:'pointer', textDecoration:'underline', textUnderlineOffset:3 }}
                  >
                    Open listing file
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
